import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { SpeciesRegulations } from '@/api/fishrules/getSpeciesRegulationsAtLocation';
import { Boundary } from '@/api/fishrules/getBoundariesAtLocation';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { Accordion } from '../Accordion/Accordion';
import { Heading } from '../Heading/Heading';
import { NextImageWrapper } from '../NextImage/NextImageWrapper';
import { accordionTitle, buildAccordionData } from './helpers/buildAccordionData';
import { ProhibitionWarning } from '../Regulations/components/ProhibitionWarning';
import { SeasonBadge } from '../Regulations/helpers/getSeasonBadge';

interface IProps {
  speciesRegulations: SpeciesRegulations;
  seasonBadge: SeasonBadge;
  boundaries?: Boundary[];
  className?: string;
}

export const RegulationCardNew = ({
  speciesRegulations,
  seasonBadge,
  boundaries,
  className,
}: IProps) => {
  const { displayName, image, regulations } = speciesRegulations;
  const isSpeciesProhibited = regulations.prohibited === 1;
  const accordionData = buildAccordionData(regulations, {
    boundaries,
    skipHarvestRules: isSpeciesProhibited,
  });
  const speciesProhibitedMessage =
    regulations.location_prohibited_message && regulations.location_prohibited_message.length > 0
      ? regulations.location_prohibited_message
      : 'It is unlawful to harvest, possess, land, purchase, sell, or exchange this species.';

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, image?.width ?? 0, image?.height, { quality });

  return (
    <div
      className={twMerge('flex flex-col gap-6 rounded-xl bg-white p-5', className)}
      data-testid="RegulationCardNew"
    >
      <div className="flex items-center">
        {isSpeciesProhibited ? (
          <span className="body-text-sm bg-error-shade rounded-lg px-2 py-1 font-semibold text-white">
            Species prohibited
          </span>
        ) : (
          <span
            className={twMerge(
              'text-abyss body-text-sm rounded-lg px-2 py-1 font-semibold',
              seasonBadge.isOpen ? 'bg-success-tint' : 'bg-error-tint',
            )}
          >
            {seasonBadge.label}
          </span>
        )}
      </div>
      <div className="flex flex-col items-center gap-2">
        {image && (
          <NextImageWrapper
            wrapperClassName="relative h-24 w-full max-w-[246px]"
            imageClassName="size-full object-contain"
            alt={displayName}
            src={image.url}
            loader={imgProxyLoader}
            fill
            sizes="(min-width: 768px) 246px, 80vw"
          />
        )}
        <Heading as="h3" variant="sm" className="text-trench mb-0 w-full text-center">
          {displayName}
        </Heading>
      </div>
      <div className="flex flex-col gap-2">
        {regulations.location_name && (
          <div className="flex w-full items-center gap-1 rounded-lg p-0 leading-normal">
            {accordionTitle('Regulation boundary', regulations.location_name)}
          </div>
        )}
        {isSpeciesProhibited && <ProhibitionWarning message={speciesProhibitedMessage} />}
        {accordionData.length > 0 && (
          <Accordion
            singleOpen={true}
            blocksData={accordionData}
            className="rounded-lg"
            buttonClassName="justify-start gap-1 rounded-lg p-0"
            chevronClassName="text-midnight flex size-7 items-center justify-center rounded p-1.5"
            contentClassName="text-trench px-0 pt-2 pb-0 body-text-sm"
          />
        )}
      </div>
    </div>
  );
};
